import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { gstRateService, GstRateInput, GstRateQueryParams } from '../services/gst-rate.service';

export const useGstRatesPage = (params: GstRateQueryParams) => {
  return useQuery({
    queryKey: ['gstRates', 'page', params],
    queryFn: () => gstRateService.getPage(params),
    placeholderData: keepPreviousData,
  });
};

export const useGstRateSummary = () => {
  return useQuery({
    queryKey: ['gstRates', 'summary'],
    queryFn: () => gstRateService.getSummary(),
  });
};

// Used only until the rates have loaded
const FALLBACK_RATE = 18;

// The GST rates every form offers: the active rates of the master, lowest first, and its default
export const useActiveGstRates = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['gstRates', 'active'],
    queryFn: () => gstRateService.getActive(),
    staleTime: 60_000,
  });

  return useMemo(() => {
    const rates = (data ?? []).map((gstRate) => Number(gstRate.rate));
    const defaultRate = Number((data ?? []).find((gstRate) => gstRate.isDefault)?.rate ?? rates[0] ?? FALLBACK_RATE);
    return {
      gstRates: data ?? [],
      rates,
      defaultRate,
      isLoaded: !!data,
      isLoading,
      isError,
      // The choices for one line: the active rates, plus the rate the line already has when that one was retired
      optionsFor: (current?: number | string | null) => {
        const value = current === '' || current === null || current === undefined ? null : Number(current);
        return value === null || !Number.isFinite(value) || rates.includes(value)
          ? rates
          : [...rates, value].sort((a, b) => a - b);
      },
    };
  }, [data, isLoading, isError]);
};

// Lists, the summary, the form dropdowns and the master data home page all show GST rates
const useInvalidateGstRates = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['gstRates'] });
    queryClient.invalidateQueries({ queryKey: ['master-data', 'overview'] });
  };
};

export const useCreateGstRate = () => {
  const invalidate = useInvalidateGstRates();
  return useMutation({
    mutationFn: (data: GstRateInput) => gstRateService.create(data),
    onSuccess: invalidate,
  });
};

export const useUpdateGstRate = () => {
  const invalidate = useInvalidateGstRates();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<GstRateInput> }) => gstRateService.update(id, data),
    onSuccess: invalidate,
  });
};

export const useSetDefaultGstRate = () => {
  const invalidate = useInvalidateGstRates();
  return useMutation({
    mutationFn: (id: string) => gstRateService.setDefault(id),
    onSuccess: invalidate,
  });
};

export const useDeleteGstRate = () => {
  const invalidate = useInvalidateGstRates();
  return useMutation({
    mutationFn: (id: string) => gstRateService.remove(id),
    onSuccess: invalidate,
  });
};
